import React from 'react';

import {
  IInventorySmartFloorPayloadUI,
  InventorySmartFloorsAPIKey,
} from '~/src/dash/apiV2/smartFloors/crud';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { CustomTextRender } from '~/src/dash/components/tables/ListViewTable/ListViewTableHead/templates';
import {
  CurrencyRender,
  EllipsisWithTooltipRender,
  HighlightedStatusTextRender,
  PercentTrendRender,
  PriceTextInputRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { TextButton } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface IInventorySmartFloorsListViewTable {
  columns: IListViewTableColumns;
}

const SelectedBidderIDOnChartRender = (props: ICustomRenderCellJSXProps): React.ReactNode => {
  const { customFunctions, customProps } = useListViewTableStore();
  const { row, value } = props;

  return (
    <TextButton
      onClick={() => customFunctions.handleSelectBidderOnChart(Number(row.ID.value), value)}
      size='small'
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'fit-content',
      }}
    >
      <IconV2
        large
        name='eye'
        color={
          customProps.selectedBidderIDForChart === Number(row.ID.value)
            ? publicaColors.darkGray
            : publicaColors.lightGray
        }
      />
    </TextButton>
  );
};

export const formatSmartFloorsResponseIntoLVTRows = (
  smartFloors: IInventorySmartFloorPayloadUI[],
  cfg: IInventorySmartFloorsListViewTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  smartFloors.forEach((sf) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: IInventorySmartFloorPayloadUI[keyof IInventorySmartFloorPayloadUI] =
        sf[responseKey as keyof IInventorySmartFloorPayloadUI];

      if (isNumeric) {
        value = Number(value) as IInventorySmartFloorPayloadUI[keyof IInventorySmartFloorPayloadUI];
      }
      const cellValue = formatValue
        ? (formatValue(value) as IInventorySmartFloorPayloadUI[keyof IInventorySmartFloorPayloadUI])
        : value;

      formattedRow[key] = {
        value: cellValue,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

export interface ISmartFloorsListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: InventorySmartFloorsAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: InventorySmartFloorsAPIKey[];
  };
  isInitialized?: boolean; // keeps track of changes to config across pages
  trends: InventorySmartFloorsAPIKey[];
  table: IInventorySmartFloorsListViewTable;
}

export const smartFloorsListViewConfig = {
  cypressClassName: 'smartfloorslistview',
  defaultState: {
    activeColumnKeys: [
      'ControlBidFloor',
      'MinBidFloor',
      'MaxBidFloor',
      'TestIncrement',
      'EstimatedRevenueUplift',
    ],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 10,
  },
  columnSelector: {
    resetKeys: ['BaseBidFloor', 'MinBidFloor', 'MaxBidFloor'],
  },
  trends: ['EstimatedRevenueUplift12HourTrend'],
  table: {
    columns: {
      CustomLeftColumn: {
        cellJSX: SelectedBidderIDOnChartRender,
        cellRenderType: 'simple_switch',
        disableSort: true,
        defaultOrderDirection: 'asc',
        key: 'CustomLeftColumn',
        label: 'Chart Display',
        requestKey: '',
        responseKey: 'isBidderIDSelected',
        valueType: 'boolean',
        cellStyle: { 'text-align': 'center', 'vertical-align': 'middle' },
        headerStyle: { 'text-align': 'center', 'min-width': '144px' },
        tKey: 'ChartDisplay',
      },
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        keyOverride: 'HeaderbidderID',
        label: 'ID',
        requestKey: '',
        responseKey: 'HeaderbidderID',
        valueType: 'string',
        tKey: 'ID',
      },
      Name: {
        // required Name field is used for bidderName
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        keyOverride: 'BidderName',
        label: 'Bidders',
        requestKey: '',
        responseKey: 'BidderName',
        valueType: 'string',
        tKey: 'Bidders',
      },
      ControlBidFloor: {
        cellJSX: PriceTextInputRender,
        cellRenderType: 'number',
        headerRenderType: 'number_select',
        headerTooltipText: 'set the starting floor for this bidder',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsBaseFloor',
        isDefaultActive: true,
        key: 'ControlBidFloor',
        label: 'Control Floor',
        requestKey: '',
        responseKey: 'ControlBidFloor',
        valueType: 'number',
        tKey: 'BaseFloor',
      },
      MinBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MinBidFloor',
        label: 'Min Bid Floor',
        requestKey: '',
        responseKey: 'MinBidFloor',
        valueType: 'number',
        tKey: 'MinBidFloor',
      },
      MaxBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MaxBidFloor',
        label: 'Max Bid Floor',
        requestKey: '',
        responseKey: 'MaxBidFloor',
        valueType: 'number',
        tKey: 'MaxBidFloor',
      },
      TestIncrement: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        key: 'TestIncrement',
        label: 'Test Increment',
        requestKey: '',
        responseKey: 'TestIncrement',
        valueType: 'number',
        headerRenderType: 'number',
        tKey: 'TestIncrement',
      },
      EstimatedRevenueUplift: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Estimated Revenue Uplift for the last 12 hours',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsEstimatedUplift',
        headerJSX: CustomTextRender,
        headerJSXStyle: {
          backgroundColor: publicaColors.eyeBlue,
          fontWeight: 500,
          borderRadius: '2px',
          minWidth: '142px',
        },
        isDefaultActive: true,
        isNumeric: true,
        key: 'EstimatedRevenueUplift',
        label: 'L12H Estimated Uplift',
        requestKey: '',
        responseKey: 'EstimatedRevenueUplift',
        trendKey: 'EstimatedRevenueUplift',
        valueType: 'number',
        tKey: 'L12HEstimatedUplift',
      },
      EstimatedRevenueUplift12HourTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Estimated Uplift Trend L12H Compared to Previous Period',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsEstimatedUpliftTrend',
        headerJSX: CustomTextRender,
        headerJSXStyle: {
          backgroundColor: publicaColors.lightGray,
          fontWeight: 500,
          borderRadius: '2px',
        },
        isDefaultActive: false,
        isSuperUserOnly: true,
        key: 'EstimatedRevenueUplift12HourTrend',
        label: '% L12H Uplift',
        requestKey: '',
        responseKey: 'EstimatedRevenueUplift12HourTrend',
        valueType: 'number',
        tKey: 'PercentL12HUplift',
      },
      Status2: {
        cellJSX: HighlightedStatusTextRender,
        headerRenderType: 'centered_text',
        headerTooltipText:
          'Running: running with currently set base floor. Pending: waiting to start with newly set base floor.',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsStatus',
        key: 'Status2',
        label: 'Status',
        requestKey: '',
        responseKey: 'Status',
        valueType: 'string',
        tKey: 'Status',
      },
    },
  },
} as ISmartFloorsListViewConfig;

// this is an almost identical config to smartFloorsListViewConfig with some columns removed
// we created a separate config to better handle the differences in the UI and to avoid confusion
export const smartFloorsListViewAdaptiveConfig = {
  cypressClassName: 'smartfloorslistview',
  defaultState: {
    activeColumnKeys: ['ControlBidFloor', 'MinBidFloor', 'MaxBidFloor', 'EstimatedRevenueUplift'],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 10,
  },
  columnSelector: {
    resetKeys: ['ControlBidFloor', 'MinBidFloor', 'MaxBidFloor'],
  },
  trends: ['EstimatedRevenueUplift12HourTrend'],
  table: {
    columns: {
      CustomLeftColumn: {
        cellJSX: SelectedBidderIDOnChartRender,
        cellRenderType: 'simple_switch',
        disableSort: true,
        defaultOrderDirection: 'asc',
        key: 'CustomLeftColumn',
        label: 'Chart Display',
        requestKey: '',
        responseKey: 'isBidderIDSelected',
        valueType: 'boolean',
        cellStyle: { 'text-align': 'center', 'vertical-align': 'middle' },
        headerStyle: { 'text-align': 'center', 'min-width': '144px' },
        tKey: 'ChartDisplay',
      },
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        keyOverride: 'HeaderbidderID',
        label: 'ID',
        requestKey: '',
        responseKey: 'HeaderbidderID',
        valueType: 'string',
        tKey: 'ID',
      },
      Name: {
        // required Name field is used for channelName
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        keyOverride: 'BidderName',
        label: 'Bidders',
        requestKey: '',
        responseKey: 'BidderName',
        valueType: 'string',
        tKey: 'Bidders',
      },
      ControlBidFloor: {
        cellJSX: PriceTextInputRender,
        cellRenderType: 'number',
        headerRenderType: 'number_select',
        headerTooltipText: 'set the starting floor for this bidder',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsBaseFloor',
        isDefaultActive: true,
        key: 'ControlBidFloor',
        label: 'Control Floor',
        requestKey: '',
        responseKey: 'ControlBidFloor',
        valueType: 'number',
        tKey: 'BaseFloor',
      },
      MinBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MinBidFloor',
        label: 'Min Bid Floor',
        requestKey: '',
        responseKey: 'MinBidFloor',
        valueType: 'number',
        tKey: 'MinBidFloor',
      },
      MaxBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MaxBidFloor',
        label: 'Max Bid Floor',
        requestKey: '',
        responseKey: 'MaxBidFloor',
        valueType: 'number',
        tKey: 'MaxBidFloor',
      },
      Status2: {
        cellJSX: HighlightedStatusTextRender,
        headerRenderType: 'centered_text',
        headerTooltipText:
          'Running: running with currently set base floor. Pending: waiting to start with newly set base floor.',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsStatus',
        key: 'Status2',
        label: 'Status',
        requestKey: '',
        responseKey: 'Status',
        valueType: 'string',
        tKey: 'Status',
      },
    },
  },
} as ISmartFloorsListViewConfig;
